import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { FileInput, Inbox, Send } from "lucide-react";
import { StatusBadge, EmptyState } from "@/components/Common";
import { UploadForm, DocRow } from "@/components/Docs";
import { NewDocRequestButton } from "@/components/DocRequestDialog";
import { api, formatErr, fmtDate, useFetch } from "@/lib/api";

const RespondDialog = ({ req, ownerType, onClose, onDone }) => {
  const [mine, setMine] = useState([]);
  const [sel, setSel] = useState([]);
  const [note, setNote] = useState("");
  useEffect(() => { if (req) api.get("/documents/mine").then((r) => setMine(r.data)); setSel([]); setNote(""); }, [req]);
  const send = async () => {
    try {
      const { data } = await api.post(`/doc-requests/${req.id}/respond`, { document_ids: sel, note });
      toast.success("Files shared privately");
      onDone(data);
    } catch (e) { toast.error(formatErr(e)); }
  };
  return (
    <Dialog open={!!req} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="respond-dialog" className="max-h-[90vh] overflow-y-auto sm:max-w-xl">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Share: {req?.title}</DialogTitle>
          <DialogDescription>Only {req?.requester?.name} and our team will be able to open the files you select.</DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          {mine.map((d) => (
            <label key={d.id} className="flex cursor-pointer items-center gap-3 rounded-md border border-stone-200 px-4 py-3 hover:bg-stone-50">
              <Checkbox data-testid={`respond-pick-${d.id}`} checked={sel.includes(d.id)} onCheckedChange={(v) => setSel(v ? [...sel, d.id] : sel.filter((x) => x !== d.id))} />
              <span className="text-sm"><span className="font-medium">{d.title}</span> <span className="text-stone-500">· {d.category}</span></span>
            </label>
          ))}
          {!mine.length && <p className="text-sm text-stone-500">You have no files yet. Upload one below.</p>}
        </div>
        <UploadForm compact ownerType={ownerType} onUploaded={(d) => { setMine([d, ...mine]); setSel([...sel, d.id]); }} />
        <Textarea data-testid="respond-note-input" rows={2} placeholder="Optional note" value={note} onChange={(e) => setNote(e.target.value)} />
        <DialogFooter><Button data-testid="respond-submit-button" disabled={!sel.length} onClick={send}>Share {sel.length || ""} file(s)</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const ReqCard = ({ r, incoming, onRespond, onDecline }) => (
  <div data-testid={`doc-request-${r.id}`} className="rounded-md border border-stone-200 bg-white p-5">
    <div className="flex flex-wrap items-start justify-between gap-3">
      <div>
        <p className="font-display font-bold text-stone-900">{r.title}</p>
        <p className="text-xs text-stone-500">{incoming ? `From ${r.requester.name}` : `To ${r.target.name}`}{!incoming && r.requester_type === "admin" ? "" : ""} · {fmtDate(r.created_at)}</p>
        {r.message && <p className="mt-2 text-sm text-stone-600">{r.message}</p>}
        {r.response_note && <p className="mt-1 text-sm italic text-stone-500">Reply: {r.response_note}</p>}
      </div>
      <StatusBadge status={r.status} />
    </div>
    {r.documents?.length > 0 && <div className="mt-4 space-y-2">{r.documents.map((d) => <DocRow key={d.id} d={d} />)}</div>}
    {incoming && r.status === "Open" && (
      <div className="mt-4 flex gap-2">
        <Button size="sm" data-testid={`respond-button-${r.id}`} onClick={() => onRespond(r)}>Share files</Button>
        <Button size="sm" variant="outline" data-testid={`decline-button-${r.id}`} onClick={() => onDecline(r)}>Decline</Button>
      </div>
    )}
  </div>
);

export const RequestsPanel = ({ ownerType, admin = false }) => {
  const { data, setData, reload } = useFetch("/doc-requests");
  const [responding, setResponding] = useState(null);
  const replace = (r) => setData((p) => ({ incoming: p.incoming.map((x) => (x.id === r.id ? r : x)), outgoing: p.outgoing.map((x) => (x.id === r.id ? r : x)) }));
  const decline = async (r) => {
    try { replace((await api.post(`/doc-requests/${r.id}/decline`, { note: "" })).data); toast.success("Request declined"); } catch (e) { toast.error(formatErr(e)); }
  };
  return (
    <div className="space-y-8">
      {admin && <NewDocRequestButton onCreated={reload} />}
      {!admin && (
        <section>
          <h3 className="mb-3 flex items-center gap-2 font-display text-lg font-bold"><Inbox className="h-4 w-4" />Requested from you</h3>
          {data?.incoming?.length === 0 && <p className="text-sm text-stone-500">No one has requested files from you.</p>}
          <div className="space-y-3">{(data?.incoming || []).map((r) => <ReqCard key={r.id} r={r} incoming onRespond={setResponding} onDecline={decline} />)}</div>
        </section>
      )}
      <section>
        <h3 className="mb-3 flex items-center gap-2 font-display text-lg font-bold"><Send className="h-4 w-4" />{admin ? "All document requests" : "Your requests"}</h3>
        {data?.outgoing?.length === 0 && <p className="text-sm text-stone-500">No requests sent yet.</p>}
        <div className="space-y-3">{(data?.outgoing || []).map((r) => <ReqCard key={r.id} r={r} />)}</div>
      </section>
      {!admin && <RespondDialog req={responding} ownerType={ownerType} onClose={() => setResponding(null)} onDone={(r) => { replace(r); setResponding(null); }} />}
    </div>
  );
};

export const MyFiles = ({ ownerType }) => {
  const { data, setData } = useFetch("/documents/mine");
  return (
    <div className="grid gap-8 lg:grid-cols-[400px_1fr]">
      <div className="h-fit lg:sticky lg:top-8"><UploadForm ownerType={ownerType} onUploaded={(d) => setData((p) => [d, ...(p || [])])} /></div>
      <div className="space-y-2">
        {data?.length === 0 && <EmptyState testId="my-files-empty" icon={FileInput} title="No files yet" desc="Upload documents to keep them ready for requests." />}
        {(data || []).map((d) => <DocRow key={d.id} d={d} editable onChange={(n) => setData((p) => p.map((x) => (x.id === n.id ? n : x)))} onDelete={(o) => setData((p) => p.filter((x) => x.id !== o.id))} />)}
      </div>
    </div>
  );
};

export const DocumentCenter = ({ ownerType }) => (
  <Tabs defaultValue="files">
    <TabsList className="mb-6">
      <TabsTrigger value="files" data-testid="tab-my-files">My files</TabsTrigger>
      <TabsTrigger value="requests" data-testid="tab-doc-requests">File requests</TabsTrigger>
    </TabsList>
    <TabsContent value="files"><MyFiles ownerType={ownerType} /></TabsContent>
    <TabsContent value="requests"><RequestsPanel ownerType={ownerType} /></TabsContent>
  </Tabs>
);
